import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../../hooks/useModal';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import InfoModal from '../../../components/ui/InfoModal';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../utils/supabase';

const PerformanceAnalytics = () => {
  const { userProfile } = useAuth();
  const { infoModal, showInfo, closeInfo } = useModal();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState('applications');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [chartData, setChartData] = useState({
    applications: [],
    interviews: [],
    hiring: []
  });
  const [applicationStatusData, setApplicationStatusData] = useState([]);
  const [topSkillsData, setTopSkillsData] = useState([]);
  const [hiringFunnelData, setHiringFunnelData] = useState([]);
  const [keyMetrics, setKeyMetrics] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [topJobs, setTopJobs] = useState([]);

  const timeRanges = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 3 months' },
    { value: '1y', label: 'Last year' }
  ];

  // Fetch analytics data from Supabase
  const fetchAnalyticsData = async () => {
    if (!userProfile?.id) return;
    
    try {
      setLoading(true);
      
      // First, try to refresh analytics to ensure we have current data
      try {
        await supabase.rpc('refresh_analytics_for_employer', {
          p_employer_id: userProfile.id,
          date_range: timeRange
        });
      } catch (rpcError) {
        console.warn('Failed to refresh analytics, continuing with existing data:', rpcError);
      }
      
      // Fetch current and previous period analytics for comparison
      const currentRange = timeRange;
      const previousRange = getPreviousRange(timeRange);
      
      const [currentAnalytics, previousAnalytics] = await Promise.all([
        supabase
          .from('employer_analytics')
          .select('*')
          .eq('employer_id', userProfile.id)
          .eq('date_range', currentRange)
          .single(),
        supabase
          .from('employer_analytics')
          .select('*')
          .eq('employer_id', userProfile.id)
          .eq('date_range', previousRange)
          .single()
      ]);
      
      const analyticsData = currentAnalytics.data;
      const previousData = previousAnalytics.data;
      
      if (analyticsData) {
        setAnalyticsData(analyticsData);
        processAnalyticsData(analyticsData, previousData);
      } else {
        // Use fallback data when no analytics exist
        const fallbackData = {
          total_applications: 0,
          active_jobs: 0,
          hires_made: 0,
          average_time_to_hire: 0,
          application_to_hire_rate: 0,
          time_trends: { applications_per_week: [], interviews_per_week: [], hires_per_week: [] },
          hiring_funnel: { applications: 0, reviewed: 0, shortlisted: 0, interviewed: 0, hired: 0 }
        };
        setAnalyticsData(fallbackData);
        processAnalyticsData(fallbackData, null);
      }
      
      // Fetch job applications for status distribution
      const { data: applicationsData, error: applicationsError } = await supabase
        .from('job_applications')
        .select(`
          id,
          status,
          job_id,
          applied_at,
          jobs!inner(employer_id, skills_required)
        `)
        .eq('jobs.employer_id', userProfile.id)
        .gte('applied_at', getStartDateFromRange(timeRange));
      
      if (applicationsError) throw applicationsError;
      processApplicationsData(applicationsData);
      
      // Fetch jobs with skills data for top performing jobs and skills analysis
      const { data: jobsData, error: jobsError } = await supabase
        .from('jobs')
        .select(`
          id,
          title,
          skills_required,
          job_applications(id, status, applied_at)
        `)
        .eq('employer_id', userProfile.id)
        .limit(20); // Get more jobs for better analysis
      
      if (jobsError) throw jobsError;
      
      // Sort jobs by application count
      const sortedJobs = jobsData
        .sort((a, b) => (b.job_applications?.length || 0) - (a.job_applications?.length || 0))
        .slice(0, 3); // Take top 3
      processJobsData(sortedJobs);
      
      // Process skills data from actual job requirements
      processSkillsData(jobsData, applicationsData);
      
      // Generate recommendations based on the data
      generateRecommendations(analyticsData, applicationsData, sortedJobs);
      
    } catch (error) {
      console.error('Error fetching performance analytics:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to get previous time range for comparison
  const getPreviousRange = (currentRange) => {
    // For comparison, we'll use the same range type but for the previous period
    // This is a simplified approach - in a production app you might want more sophisticated logic
    switch (currentRange) {
      case '7d': return '7d'; // Compare with previous week's data if available
      case '30d': return '30d'; // Compare with previous month's data if available  
      case '90d': return '90d'; // Compare with previous quarter's data if available
      case '1y': return '1y'; // Compare with previous year's data if available
      default: return '30d';
    }
  };

  // Calculate percentage change between current and previous values
  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    if (Math.abs(change) < 0.1) return null; // Don't show very small changes
    return change.toFixed(1);
  };

  // Process the analytics data for charts and metrics
  const processAnalyticsData = (data, previousData = null) => {
    if (!data) return;
    
    // Process time trends for chart data
    if (data.time_trends) {
      const appWeeks = data.time_trends.applications_per_week || [];
      const intWeeks = data.time_trends.interviews_per_week || [];
      const hireWeeks = data.time_trends.hires_per_week || [];
      
      // Ensure we have at least 2 data points for meaningful charts
      const minWeeks = Math.max(2, Math.max(appWeeks.length, intWeeks.length, hireWeeks.length));
      const now = new Date();
      
      // Pad arrays to ensure consistent length and at least 2 data points
      const paddedAppWeeks = Array(minWeeks).fill(0).map((_, i) => appWeeks[i] || 0);
      const paddedIntWeeks = Array(minWeeks).fill(0).map((_, i) => intWeeks[i] || 0);
      const paddedHireWeeks = Array(minWeeks).fill(0).map((_, i) => hireWeeks[i] || 0);
      
      // Generate chart data for applications
      const applicationsChartData = paddedAppWeeks.map((value, index) => {
        const date = new Date(now);
        date.setDate(date.getDate() - (minWeeks - index - 1) * 7);
        return {
          date: date.toISOString().split('T')[0],
          value: value || 0,
          label: `Week ${index + 1}`
        };
      }).reverse();
      
      // Generate chart data for interviews
      const interviewsChartData = paddedIntWeeks.map((value, index) => {
        const date = new Date(now);
        date.setDate(date.getDate() - (minWeeks - index - 1) * 7);
        return {
          date: date.toISOString().split('T')[0],
          value: value || 0,
          label: `Week ${index + 1}`
        };
      }).reverse();
      
      // Generate chart data for hiring
      const hiringChartData = paddedHireWeeks.map((value, index) => {
        const date = new Date(now);
        date.setDate(date.getDate() - (minWeeks - index - 1) * 7);
        return {
          date: date.toISOString().split('T')[0],
          value: value || 0,
          label: `Week ${index + 1}`
        };
      }).reverse();
      
      console.log('Chart data generated:', {
        applications: applicationsChartData,
        interviews: interviewsChartData,
        hiring: hiringChartData
      });
      
      setChartData({
        applications: applicationsChartData,
        interviews: interviewsChartData,
        hiring: hiringChartData
      });
    } else {
      // Create default chart data with 2 data points when no time_trends exist
      const now = new Date();
      const defaultData = [
        {
          date: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          value: 0,
          label: 'Week 1'
        },
        {
          date: now.toISOString().split('T')[0],
          value: data.total_applications || 0,
          label: 'Week 2'
        }
      ];
      
      setChartData({
        applications: defaultData,
        interviews: [{...defaultData[0]}, {...defaultData[1], value: data.interviews_completed || 0}],
        hiring: [{...defaultData[0]}, {...defaultData[1], value: data.hires_made || 0}]
      });
    }
    
    // Process hiring funnel data
    if (data.hiring_funnel && typeof data.hiring_funnel === 'object' && !Array.isArray(data.hiring_funnel)) {
      const funnel = data.hiring_funnel;
      const totalApplications = funnel.applications || 0;
      
      const funnelData = [
        { 
          stage: 'Applications', 
          count: totalApplications, 
          percentage: 100 
        },
        { 
          stage: 'Reviewed', 
          count: funnel.reviewed || 0, 
          percentage: totalApplications > 0 ? Math.round((funnel.reviewed || 0) / totalApplications * 100) : 0 
        },
        { 
          stage: 'Shortlisted', 
          count: funnel.shortlisted || 0, 
          percentage: totalApplications > 0 ? Math.round((funnel.shortlisted || 0) / totalApplications * 100) : 0 
        },
        { 
          stage: 'Interviewed', 
          count: funnel.interviewed || 0, 
          percentage: totalApplications > 0 ? Math.round((funnel.interviewed || 0) / totalApplications * 100) : 0 
        },
        { 
          stage: 'Hired', 
          count: funnel.hired || 0, 
          percentage: totalApplications > 0 ? Math.round((funnel.hired || 0) / totalApplications * 100) : 0 
        }
      ];
      
      setHiringFunnelData(funnelData);
    } else {
      // Create default funnel data when hiring_funnel is empty or malformed
      const totalApps = data.total_applications || 0;
      const totalHires = data.hires_made || 0;
      const totalInterviews = data.interviews_completed || 0;
      
      const funnelData = [
        { stage: 'Applications', count: totalApps, percentage: 100 },
        { stage: 'Reviewed', count: totalApps, percentage: totalApps > 0 ? 100 : 0 },
        { stage: 'Shortlisted', count: Math.max(totalInterviews, totalHires), percentage: totalApps > 0 ? Math.round(Math.max(totalInterviews, totalHires) / totalApps * 100) : 0 },
        { stage: 'Interviewed', count: totalInterviews, percentage: totalApps > 0 ? Math.round(totalInterviews / totalApps * 100) : 0 },
        { stage: 'Hired', count: totalHires, percentage: totalApps > 0 ? Math.round(totalHires / totalApps * 100) : 0 }
      ];
      
      setHiringFunnelData(funnelData);
    }
    
    // Process key metrics with real percentage changes
    const applicationsChange = previousData ? calculatePercentageChange(data.total_applications, previousData.total_applications) : null;
    const hiresChange = previousData ? calculatePercentageChange(data.hires_made, previousData.hires_made) : null;
    const timeToHireChange = previousData ? calculatePercentageChange(data.average_time_to_hire, previousData.average_time_to_hire) : null;
    const rateChange = previousData ? calculatePercentageChange(data.application_to_hire_rate, previousData.application_to_hire_rate) : null;
    const activeJobsChange = previousData ? calculatePercentageChange(data.active_jobs, previousData.active_jobs) : null;
    const interviewsChange = previousData ? calculatePercentageChange(data.interviews_completed, previousData.interviews_completed) : null;
    
    const metrics = [
      {
        title: 'Total Applications',
        value: data.total_applications?.toString() || '0',
        change: applicationsChange ? `${applicationsChange > 0 ? '+' : ''}${applicationsChange}%` : null,
        trend: applicationsChange ? (applicationsChange > 0 ? 'up' : 'down') : 'neutral',
        icon: 'Users',
        color: 'text-primary'
      },
      {
        title: 'Interviews Completed',
        value: data.interviews_completed?.toString() || '0',
        change: interviewsChange ? `${interviewsChange > 0 ? '+' : ''}${interviewsChange}%` : null,
        trend: interviewsChange ? (interviewsChange > 0 ? 'up' : 'down') : 'neutral',
        icon: 'MessageCircle',
        color: 'text-accent'
      },
      {
        title: 'Hired VAs',
        value: data.hires_made?.toString() || '0',
        change: hiresChange ? `${hiresChange > 0 ? '+' : ''}${hiresChange}%` : null,
        trend: hiresChange ? (hiresChange > 0 ? 'up' : 'down') : 'neutral',
        icon: 'UserCheck',
        color: 'text-success'
      },
      {
        title: 'Average Time to Hire',
        value: data.average_time_to_hire ? `${Math.round(data.average_time_to_hire)} days` : '0 days',
        change: timeToHireChange ? `${timeToHireChange > 0 ? '+' : ''}${timeToHireChange}%` : null,
        trend: timeToHireChange ? (timeToHireChange < 0 ? 'up' : 'down') : 'neutral', // Lower time is better
        icon: 'Clock',
        color: 'text-warning'
      },
      {
        title: 'Hire Rate',
        value: data.application_to_hire_rate ? `${Math.round(data.application_to_hire_rate)}%` : '0%',
        change: rateChange ? `${rateChange > 0 ? '+' : ''}${rateChange}%` : null,
        trend: rateChange ? (rateChange > 0 ? 'up' : 'down') : 'neutral',
        icon: 'TrendingUp',
        color: 'text-primary'
      },
      {
        title: 'Active Jobs',
        value: data.active_jobs?.toString() || '0',
        change: activeJobsChange ? `${activeJobsChange > 0 ? '+' : ''}${activeJobsChange}%` : null,
        trend: activeJobsChange ? (activeJobsChange > 0 ? 'up' : 'down') : 'neutral',
        icon: 'Target',
        color: 'text-success'
      }
    ];
    
    setKeyMetrics(metrics);
  };
  
  // Process application data for status distribution
  const processApplicationsData = (applications) => {
    if (!applications || applications.length === 0) {
      // Show empty state with 0 values instead of hiding the chart
      setApplicationStatusData([
        { name: 'Pending', value: 100, color: '#F59E0B', count: 0 },
        { name: 'Shortlisted', value: 0, color: '#3B82F6', count: 0 },
        { name: 'Interviewed', value: 0, color: '#8B5CF6', count: 0 },
        { name: 'Hired', value: 0, color: '#10B981', count: 0 },
        { name: 'Rejected', value: 0, color: '#EF4444', count: 0 }
      ]);
      return;
    }
    
    const statusCounts = {
      pending: 0,
      shortlisted: 0,
      interviewed: 0,
      hired: 0,
      rejected: 0
    };
    
    // Count applications by status - handle different status values
    applications.forEach(app => {
      const status = app.status?.toLowerCase();
      if (status === 'pending' || !status) statusCounts.pending++;
      else if (status === 'shortlisted') statusCounts.shortlisted++;
      else if (status === 'interviewed') statusCounts.interviewed++;
      else if (status === 'hired' || status === 'accepted') statusCounts.hired++;
      else if (status === 'rejected') statusCounts.rejected++;
      else statusCounts.pending++; // Default unknown statuses to pending
    });
    
    const total = applications.length;
    
    // Calculate percentages
    const statusData = [
      { 
        name: 'Pending', 
        value: total > 0 ? Math.round((statusCounts.pending / total) * 100) : 0, 
        color: '#F59E0B',
        count: statusCounts.pending
      },
      { 
        name: 'Shortlisted', 
        value: total > 0 ? Math.round((statusCounts.shortlisted / total) * 100) : 0, 
        color: '#3B82F6',
        count: statusCounts.shortlisted
      },
      { 
        name: 'Interviewed', 
        value: total > 0 ? Math.round((statusCounts.interviewed / total) * 100) : 0, 
        color: '#8B5CF6',
        count: statusCounts.interviewed
      },
      { 
        name: 'Hired', 
        value: total > 0 ? Math.round((statusCounts.hired / total) * 100) : 0, 
        color: '#10B981',
        count: statusCounts.hired
      },
      { 
        name: 'Rejected', 
        value: total > 0 ? Math.round((statusCounts.rejected / total) * 100) : 0, 
        color: '#EF4444',
        count: statusCounts.rejected
      }
    ];
    
    console.log('Application status data processed:', statusData);
    setApplicationStatusData(statusData);
  };
  
  // Process jobs data for top jobs widget
  const processJobsData = (jobs) => {
    if (!jobs || jobs.length === 0) {
      setTopJobs([]);
      return;
    }
    
    // Process top jobs - remove mock views data
    const topJobsData = jobs.map(job => ({
      title: job.title,
      applications: job.job_applications?.length || 0,
      hires: job.job_applications?.filter(app => app.status === 'accepted').length || 0
    }));
    
    setTopJobs(topJobsData);
  };

  // Process skills data from actual job requirements
  const processSkillsData = (jobs, applications) => {
    if (!jobs || jobs.length === 0) {
      setTopSkillsData([]);
      return;
    }
    
    // Count skills from job requirements
    const skillCounts = {};
    let totalJobsWithSkills = 0;
    
    jobs.forEach(job => {
      if (job.skills_required && Array.isArray(job.skills_required)) {
        totalJobsWithSkills++;
        job.skills_required.forEach(skill => {
          skillCounts[skill] = (skillCounts[skill] || 0) + 1;
        });
      }
    });
    
    // Convert to array and sort by count
    const skillsArray = Object.entries(skillCounts)
      .map(([skill, count]) => ({
        skill,
        count,
        percentage: totalJobsWithSkills > 0 ? Math.round((count / totalJobsWithSkills) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6); // Top 6 skills
    
    setTopSkillsData(skillsArray);
  };
  
  // Generate recommendations based on analytics
  const generateRecommendations = (analytics, applications, jobs) => {
    const recommendations = [];
    
    if (!analytics) {
      setRecommendations([]);
      return;
    }
    
    // Check response time based on actual data
    if (analytics.average_time_to_hire > 15) {
      recommendations.push({
        title: 'Speed up hiring process',
        description: `Your average time to hire is ${Math.round(analytics.average_time_to_hire)} days. Consider streamlining your process.`,
        icon: 'Clock',
        color: 'warning'
      });
    }
    
    // Check application to hire rate
    if (analytics.application_to_hire_rate < 5) {
      recommendations.push({
        title: 'Improve candidate selection',
        description: `Your hire rate is ${Math.round(analytics.application_to_hire_rate)}%. Consider refining job requirements.`,
        icon: 'UserCheck',
        color: 'primary'
      });
    }
    
    // Check application volume
    if (analytics.total_applications < 5) {
      recommendations.push({
        title: 'Increase job visibility',
        description: `You've received ${analytics.total_applications} applications. Consider promoting your jobs more.`,
        icon: 'TrendingUp',
        color: 'success'
      });
    }
    
    // Check active jobs
    if (analytics.active_jobs === 0) {
      recommendations.push({
        title: 'Post new job opportunities',
        description: 'You have no active jobs. Post new positions to attract candidates.',
        icon: 'Plus',
        color: 'primary'
      });
    }
    
    // Check interview completion rate
    const interviewRate = analytics.interviews_scheduled > 0 
      ? (analytics.interviews_completed / analytics.interviews_scheduled) * 100 
      : 0;
    
    if (interviewRate < 80 && analytics.interviews_scheduled > 0) {
      recommendations.push({
        title: 'Improve interview completion',
        description: `${Math.round(interviewRate)}% of scheduled interviews are completed. Follow up with candidates.`,
        icon: 'MessageCircle',
        color: 'warning'
      });
    }
    
    // Analysis based on jobs data
    if (jobs && jobs.length > 0) {
      const avgApplicationsPerJob = jobs.reduce((sum, job) => sum + (job.job_applications?.length || 0), 0) / jobs.length;
      
      if (avgApplicationsPerJob < 3) {
        recommendations.push({
          title: 'Optimize job descriptions',
          description: `Your jobs average ${Math.round(avgApplicationsPerJob)} applications. Improve descriptions and requirements.`,
          icon: 'Edit',
          color: 'info'
        });
      }
    }
    
    // Show top 3 most relevant recommendations
    setRecommendations(recommendations.slice(0, 3));
  };
  
  // Helper function to get start date based on time range
  const getStartDateFromRange = (range) => {
    const now = new Date();
    
    switch (range) {
      case '7d':
        return new Date(now.setDate(now.getDate() - 7)).toISOString();
      case '30d':
        return new Date(now.setDate(now.getDate() - 30)).toISOString();
      case '90d':
        return new Date(now.setDate(now.getDate() - 90)).toISOString();
      case '1y':
        return new Date(now.setFullYear(now.getFullYear() - 1)).toISOString();
      default:
        return new Date(now.setDate(now.getDate() - 30)).toISOString();
    }
  };
  
  // Fetch data when component mounts or time range changes
  useEffect(() => {
    if (userProfile?.id) {
      fetchAnalyticsData();
    }
  }, [userProfile, timeRange]);
  
  const selectedChartData = useMemo(() => {
    const data = chartData[selectedMetric] || [];
    console.log(`Selected chart data for ${selectedMetric}:`, data);
    return data;
  }, [selectedMetric, chartData]);

  const handleExportData = () => {
    if (!analyticsData) return;
    
    // Convert the analytics data to CSV
    try {
      // Prepare data for CSV
      const metricsData = keyMetrics.map(metric => ({
        Metric: metric.title,
        Value: metric.value,
        Change: metric.change
      }));
      
      const funnelData = hiringFunnelData.map(stage => ({
        Stage: stage.stage,
        Count: stage.count,
        Percentage: `${stage.percentage}%`
      }));
      
      // Create CSV content
      let csvContent = "data:text/csv;charset=utf-8,";
      
      // Add metrics section
      csvContent += "EMPLOYER PERFORMANCE METRICS\r\n\r\n";
      csvContent += "Metric,Value,Change\r\n";
      metricsData.forEach(row => {
        csvContent += `${row.Metric},${row.Value},${row.Change}\r\n`;
      });
      
      // Add funnel section
      csvContent += "\r\nHIRING FUNNEL\r\n";
      csvContent += "Stage,Count,Percentage\r\n";
      funnelData.forEach(row => {
        csvContent += `${row.Stage},${row.Count},${row.Percentage}\r\n`;
      });
      
      // Encode and download
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `va-sprouts-analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting analytics data:', error);
      showInfo({
        title: 'Export Error',
        message: 'There was an error exporting the data. Please try again later.',
        type: 'error'
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            disabled={loading}
          >
            {timeRanges.map(range => (
              <option key={range.value} value={range.value}>
                {range.label}
              </option>
            ))}
          </select>

          <select
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="w-32 px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            disabled={loading}
          >
            <option value="applications">Applications</option>
            <option value="interviews">Interviews</option>
            <option value="hiring">Hiring</option>
          </select>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            iconName="TrendingUp"
            onClick={() => navigate('/advanced-analytics')}
            disabled={loading}
          >
            Advanced Analytics
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            onClick={handleExportData}
            disabled={loading || !analyticsData}
          >
            Export Data
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="Loader" size={40} className="text-primary animate-spin mb-4" />
          <p className="text-text-secondary">Loading analytics data...</p>
        </div>
      ) : (
        <>
          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {keyMetrics.map((metric, index) => (
              <div key={index} className="bg-surface rounded-lg border border-border p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-text-secondary">{metric.title}</p>
                    <p className="text-2xl font-bold text-text-primary mt-1">{metric.value}</p>
                    <div className="flex items-center mt-2">
                      <Icon 
                        name={metric.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
                        size={16} 
                        className={metric.trend === 'up' ? 'text-success' : 'text-error'} 
                      />
                      <span className={`text-sm ml-1 ${metric.trend === 'up' ? 'text-success' : 'text-error'}`}>
                        {metric.change}
                      </span>
                      <span className="text-sm text-text-secondary ml-1">vs last period</span>
                    </div>
                  </div>
                  <div className={`w-12 h-12 rounded-lg bg-opacity-10 flex items-center justify-center ${
                    metric.color === 'text-primary' ? 'bg-primary' :
                    metric.color === 'text-accent' ? 'bg-accent' :
                    metric.color === 'text-success' ? 'bg-success' :
                    metric.color === 'text-warning' ? 'bg-warning' : 'bg-primary'
                  }`}>
                    <Icon name={metric.icon} size={24} className={metric.color} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Main Chart */}
          <div className="bg-surface rounded-lg border border-border p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-text-primary capitalize">
                {selectedMetric.replace(/([A-Z])/g, ' $1').trim()} Over Time
              </h3>
              <div className="text-sm text-text-secondary">
                {timeRanges.find(r => r.value === timeRange)?.label}
              </div>
            </div>
            
            <div className="h-80">
              {selectedChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis 
                      dataKey="label" 
                      stroke="#6B7280"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#6B7280"
                      fontSize={12}
                    />
                    <Tooltip content={<CustomTooltip selectedMetric={selectedMetric} />} />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-text-secondary text-center">
                    No data available for the selected time range.<br />
                    Try selecting a different time range or metric.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Application Status Distribution */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-6">Application Status</h3>
              
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={applicationStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {applicationStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, props) => [
                        `${value}% (${props.payload.count || 0} applications)`, 
                        'Status'
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-4">
                {applicationStatusData.map((item, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="text-sm text-text-secondary">{item.name}</span>
                    <span className="text-sm font-medium text-text-primary ml-auto">
                      {item.count || 0} ({item.value}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Skills */}
            <div className="bg-surface rounded-lg border border-border p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-6">Most Requested Skills</h3>
              
              <div className="space-y-4">
                {topSkillsData.length > 0 ? (
                  topSkillsData.map((skill, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-text-primary">
                            {skill.skill}
                          </span>
                          <span className="text-sm text-text-secondary">
                            {skill.count} applications
                          </span>
                        </div>
                        <div className="w-full bg-secondary-200 rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${skill.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="h-64 flex items-center justify-center">
                    <p className="text-text-secondary text-center">
                      No skills data available.<br />
                      Add skills to your job postings to see this chart.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Hiring Funnel */}
          <div className="bg-surface rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-6">Hiring Funnel</h3>
            
            <div className="space-y-4">
              {hiringFunnelData.length > 0 && hiringFunnelData[0].count > 0 ? (
                hiringFunnelData.map((stage, index) => (
                  <div key={index} className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-text-primary">
                        {stage.stage}
                      </span>
                      <div className="text-sm text-text-secondary">
                        {stage.count} candidates ({stage.percentage}%)
                      </div>
                    </div>
                    <div className="w-full bg-secondary-200 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-primary to-accent h-3 rounded-full transition-all duration-500"
                        style={{ width: `${stage.percentage}%` }}
                      />
                    </div>
                    {index < hiringFunnelData.length - 1 && (
                      <div className="absolute right-0 top-8 text-xs text-text-muted">
                        {hiringFunnelData[index].count > 0 
                          ? Math.round((hiringFunnelData[index + 1].count / hiringFunnelData[index].count) * 100)
                          : 0}% conversion
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="h-32 flex items-center justify-center">
                  <p className="text-text-secondary text-center">
                    No hiring funnel data available.<br />
                    Add applications and process candidates to see this chart.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Performance Insights */}
          <div className="bg-surface rounded-lg border border-border p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-6">Performance Insights</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-text-primary mb-3">Top Performing Jobs</h4>
                <div className="space-y-3">
                  {topJobs.length > 0 ? (
                    topJobs.map((job, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-text-primary">{job.title}</p>
                          <p className="text-xs text-text-secondary">{job.applications} applications</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-primary">{job.hires}</p>
                          <p className="text-xs text-text-secondary">hires</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="h-32 flex items-center justify-center">
                      <p className="text-text-secondary text-center">
                        No job performance data available.<br />
                        Add more job postings to see top performers.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-text-primary mb-3">Recommendations</h4>
                <div className="space-y-3">
                  {recommendations.map((rec, index) => (
                    <div key={index} className={`flex items-start space-x-3 p-3 bg-${rec.color}-50 rounded-lg`}>
                      <Icon name={rec.icon} size={16} className={`text-${rec.color} mt-0.5`} />
                      <div>
                        <p className={`text-sm font-medium text-${rec.color}`}>{rec.title}</p>
                        <p className="text-xs text-text-secondary">{rec.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Info Modal */}
      {infoModal && (
        <InfoModal
          title={infoModal.title}
          message={infoModal.message}
          type={infoModal.type}
          buttonLabel={infoModal.buttonLabel}
          onClose={closeInfo}
        />
      )}
    </div>
  );
};

// Custom tooltip component for the chart
const CustomTooltip = ({ active, payload, label, selectedMetric }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-surface border border-border rounded-lg p-3 shadow-lg">
        <p className="text-sm font-medium text-text-primary">{label}</p>
        <p className="text-sm text-primary">
          {selectedMetric === 'applications' && 'Applications: '}
          {selectedMetric === 'interviews' && 'Interviews: '}
          {selectedMetric === 'hiring' && 'Hired: '}
          <span className="font-semibold">{payload[0]?.value}</span>
        </p>
      </div>
    );
  }
  return null;
};

export default PerformanceAnalytics;

